import { Table, TableBody, TableRow } from "@mui/material";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type {
  ExtendedVisionUser,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import { tk } from "@thalamos/vision-common";

import { DataCell } from "../../../components/IncidentWorkItems/IncidentTable/cells/DataCell.js";
import { columns } from "../../../components/IncidentWorkItems/IncidentTable/columns.js";
import {
  ColumnHeaderRow,
  EmptySectionRow,
  LoadingSectionRow,
} from "../../../components/IncidentWorkItems/IncidentTable/rows/index.js";
import { buildArchiveRows } from "./buildRows.js";

export type DesktopArchiveTableProps = {
  incidents: IncidentWorkItemWithContext[];
  user: ExtendedVisionUser | undefined;
  isLoading: boolean;
  onDownloadPdf: (incident: IncidentWorkItemWithContext) => Promise<void>;
};

export function DesktopArchiveTable({
  incidents,
  user,
  isLoading,
  onDownloadPdf,
}: DesktopArchiveTableProps) {
  const { t } = useTranslation();

  const rows = buildArchiveRows({ incidents, user, onDownloadPdf, t });

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    // `data` is rebuilt on every render, and each new array would queue a
    // page-index reset whose state update re-renders again, endlessly.
    autoResetPageIndex: false,
  });

  const colSpan = columns.length;

  return (
    <Table
      sx={{
        tableLayout: "auto",
        width: "100%",
        borderCollapse: "collapse",
        "& .MuiTableCell-root": {
          padding: "0.5rem 0.75rem",
          wordBreak: "break-word",
        },
        "& .structural-row .MuiTableCell-root": {
          border: "none",
          padding: 0,
        },
      }}
    >
      <TableBody>
        {isLoading ? (
          <LoadingSectionRow colSpan={colSpan} />
        ) : rows.length === 0 ? (
          <EmptySectionRow
            label={tk("archivePage.noFormsCompleted")}
            colSpan={colSpan}
          />
        ) : (
          <>
            <ColumnHeaderRow table={table} />
            {rows.map((row) => {
              const tableRow = table
                .getRowModel()
                .rows.find((r) => r.id === row.id);
              if (!tableRow) return null;

              const cells = tableRow.getVisibleCells();

              return (
                <TableRow key={tableRow.id}>
                  {cells.map((cell) => (
                    <DataCell key={cell.id} row={row} columnId={cell.column.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </DataCell>
                  ))}
                </TableRow>
              );
            })}
          </>
        )}
      </TableBody>
    </Table>
  );
}
